import styled from 'styled-components';

/* ------------------------------------------------------------------ */
/* Shared modal shell — identical tokens/pattern to CreateTicketModal   */
/* ------------------------------------------------------------------ */

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: ${({ theme }) => theme.landingSpacing.md};
`;

export const ModalContent = styled.div`
  background: ${({ theme }) => theme.landing.cardBg};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  width: 100%;
  max-width: 620px;
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  padding: ${({ theme }) => theme.landingSpacing.lg};
  display: flex;
  flex-direction: column;
  box-shadow:
    0px 20px 45px -15px rgba(15, 23, 42, 0.25),
    0 0 0 1px rgba(15, 23, 42, 0.05);
  z-index: 10000;
  animation: slideDown 0.2s ease;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    width: 100%;
    padding: ${({ theme }) => theme.landingSpacing.md};
  }
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.landingSpacing.md};
`;

export const ModalTitle = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.primary};
  font-weight: 700;
  font-size: 1.4rem;
  letter-spacing: -0.02em;
  line-height: 1.4;
  color: ${({ theme }) => theme.landing.text};
`;

export const ModalCloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  background: transparent;
  color: ${({ theme }) => theme.landing.textSecondary};
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.landing.iconBg};
    color: ${({ theme }) => theme.landing.text};
  }
`;

export const ModalBody = styled.div`
  padding: ${({ theme }) => theme.landingSpacing.sm} 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.landingSpacing.lg};
`;

export const ModalFooter = styled.footer`
  margin-top: ${({ theme }) => theme.landingSpacing.lg};
  padding-top: ${({ theme }) => theme.landingSpacing.lg};
  border-top: 1px solid ${({ theme }) => theme.landing.cardBorder};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const FooterActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.landingSpacing.sm};
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    justify-content: stretch;

    button {
      flex: 1;
    }
  }
`;

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${({ theme }) => theme.landingSpacing.md};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.landingSpacing.xs};
`;

export const InputLabel = styled.label`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-weight: 500;
  font-size: ${({ theme }) => theme.fontSizes.small};
  color: ${({ theme }) => theme.landing.textSecondary};
`;

export const TextInput = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 ${({ theme }) => theme.landingSpacing.md};
  border: 1px solid
    ${({ theme, $hasError }) => ($hasError ? theme.landing.errorColor : theme.landing.cardBorder)};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  background: ${({ theme }) => theme.landing.inputBg};
  color: ${({ theme }) => theme.landing.text};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fontSizes.normal};
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.landing.buttonBg};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: ${({ theme }) => theme.landing.iconBg};
  }

  &::placeholder {
    color: ${({ theme }) => theme.landing.textMuted};
  }
`;

export const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: ${({ theme }) => theme.landingSpacing.md};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  background: ${({ theme }) => theme.landing.inputBg};
  color: ${({ theme }) => theme.landing.text};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fontSizes.normal};
  line-height: 1.5;
  resize: vertical;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.landing.buttonBg};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }

  &::placeholder {
    color: ${({ theme }) => theme.landing.textMuted};
  }
`;

export const ErrorText = styled.p`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 11px;
  color: ${({ theme }) => theme.landing.errorColor};
  margin: 0;
`;

export const HelperNote = styled.p`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 11px;
  color: ${({ theme }) => theme.landing.textSecondary};
  margin: 0;
`;

/* ------------------------------------------------------------------ */
/* Logo upload — one block per logo (light/dark), reused twice          */
/* ------------------------------------------------------------------ */

export const LogoUploadRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${({ theme }) => theme.landingSpacing.md};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

export const LogoUploadBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.landingSpacing.xs};
  padding: ${({ theme }) => theme.landingSpacing.md};
  border: 1px dashed ${({ theme }) => theme.landing.cardBorder};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  background: ${({ theme }) => theme.landing.cardBg};
`;

export const LogoPreviewRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.landingSpacing.sm};
`;

export const LogoThumb = styled.div`
  width: 36px;
  height: 36px;
  border-radius: ${({ theme }) => theme.landingBorderRadius.sm};
  background: ${({ theme, slot }) => (slot === 'light' ? '#ffffff' : '#374151')};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ theme }) => theme.landing.textSecondary};
  overflow: hidden;

  svg {
    width: 16px;
    height: 16px;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

export const LogoFileName = styled.span`
  font-family: 'SFMono-Regular', ui-monospace, Menlo, Consolas, monospace;
  font-size: 12px;
  color: ${({ theme }) => theme.landing.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
`;

export const UploadLogoButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px ${({ theme }) => theme.landingSpacing.sm};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  background: ${({ theme }) => theme.landing.cardBg};
  color: ${({ theme }) => theme.landing.text};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;
  align-self: flex-start;

  svg {
    width: 13px;
    height: 13px;
  }

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.landing.iconBg};
    border-color: ${({ theme }) => theme.landing.textMuted};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const RemoveLogoButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: ${({ theme }) => theme.landingBorderRadius.sm};
  background: transparent;
  color: ${({ theme }) => theme.landing.textSecondary};
  cursor: pointer;
  flex-shrink: 0;
  transition:
    background 0.15s ease,
    color 0.15s ease;

  svg {
    width: 13px;
    height: 13px;
  }

  &:hover {
    background: rgba(239, 68, 68, 0.1);
    color: ${({ theme }) => theme.landing.errorColor};
  }
`;

export const HiddenFileInput = styled.input`
  display: none;
`;

/* ------------------------------------------------------------------ */
/* Footer buttons                                                       */
/* ------------------------------------------------------------------ */

export const ModalCancelButton = styled.button`
  padding: ${({ theme }) => theme.landingSpacing.sm} ${({ theme }) => theme.landingSpacing.lg};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  background: transparent;
  color: ${({ theme }) => theme.landing.text};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-weight: 500;
  font-size: ${({ theme }) => theme.fontSizes.normal};
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.landing.iconBg};
  }
`;

export const ModalSubmitButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: ${({ theme }) => theme.landingSpacing.sm} ${({ theme }) => theme.landingSpacing.lg};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  border: none;
  background: ${({ theme }) => theme.landing.buttonBg};
  color: #ffffff;
  font-family: ${({ theme }) => theme.fonts.primary};
  font-weight: 500;
  font-size: ${({ theme }) => theme.fontSizes.normal};
  cursor: pointer;
  transition:
    opacity 0.2s ease,
    transform 0.1s ease;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover:not(:disabled) {
    opacity: 0.92;
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const LogoActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

export const LogoIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  background: transparent;
  color: ${({ theme }) => theme.landing.textSecondary};
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease;

  svg {
    width: 15px;
    height: 15px;
  }

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.landing.iconBg};
    color: ${({ theme }) => theme.landing.text};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
